import React, { useState } from "react";
import { db } from "@/lib/firebase";
import { collectionGroup, onSnapshot, orderBy, query, limit, doc, updateDoc, serverTimestamp, collection, getDocs } from "firebase/firestore";
import useUserProfile from "@/hooks/useUserProfile";
import useCoopMemberRole from "@/hooks/useCoopMemberRole";
import { ToastContext } from "@/components/ToastProvider.jsx";
import { formatDT, fromNow } from "@/utils/dates";

export default function DeliveryIssues(){
  const { profile } = useUserProfile();
  const { isFinancial } = useCoopMemberRole();
  const isStaff = isFinancial;
  const { notify } = React.useContext(ToastContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = React.useState("open"); // open | closed | all
  const [error, setError] = React.useState(null);
  const [fallbackMode, setFallbackMode] = React.useState(false);

  React.useEffect(()=>{
    if (!isStaff) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    // Try collectionGroup first (more efficient)
    const tryCollectionGroup = () => {
      try {
        const q = query(collectionGroup(db, 'issues'), orderBy('createdAt','desc'), limit(200));
        const unsub = onSnapshot(q, (snap)=>{
          const list = snap.docs.map(d=>{
            // parent path: deliveries/{deliveryId}/issues/{iid}
            const deliveryRef = d.ref.parent.parent;
            const deliveryId = deliveryRef ? deliveryRef.id : null;
            return { id: d.id, deliveryId, ...d.data() };
          });
          setRows(list);
          setLoading(false);
          setFallbackMode(false);
        }, (e)=>{
          // CollectionGroup query failed, falling back to manual collection queries
          setFallbackMode(true);
          // Fall back to manual collection queries
          tryManualQueries();
        });
        return unsub;
              } catch (e) {
          // CollectionGroup query failed, falling back to manual collection queries
          setFallbackMode(true);
          tryManualQueries();
          return () => {};
        }
    };

    // Fallback: manually query deliveries and then their issues
    const tryManualQueries = async () => {
      try {
        const deliveriesQuery = query(collection(db, 'deliveries'), limit(50));
        const deliveriesSnap = await getDocs(deliveriesQuery);
        
        const allIssues = [];
        for (const deliveryDoc of deliveriesSnap.docs) {
          try {
            const issuesQuery = query(
              collection(db, 'deliveries', deliveryDoc.id, 'issues'),
              orderBy('createdAt', 'desc'),
              limit(10)
            );
            const issuesSnap = await getDocs(issuesQuery);
            const issues = issuesSnap.docs.map(d => ({
              id: d.id,
              deliveryId: deliveryDoc.id,
              ...d.data()
            }));
            allIssues.push(...issues);
          } catch (e) {
            // Failed to fetch issues for delivery
          }
        }
        
        // Sort by creation date (newest first)
        allIssues.sort((a, b) => {
          const aTime = a.createdAt?.toDate?.() || new Date(0);
          const bTime = b.createdAt?.toDate?.() || new Date(0);
          return bTime - aTime;
        });
        
        setRows(allIssues.slice(0, 200));
        setLoading(false);
      } catch (e) {
        // Manual queries also failed
        setError('Failed to load delivery issues. Please check your permissions or try again later.');
        setLoading(false);
      }
    };

    const unsub = tryCollectionGroup();
    return ()=>unsub();
  }, [isStaff]);

  const filtered = React.useMemo(()=>{
    if (statusFilter === 'all') return rows;
    return rows.filter(r => String(r.status||'') === statusFilter);
  }, [rows, statusFilter]);

  async function closeIssue(row){
    try{
      if (!row.deliveryId || !row.id) return;
      await updateDoc(doc(db, 'deliveries', row.deliveryId, 'issues', row.id), { status:'closed', closedAt: serverTimestamp() });
      notify('Issue closed.', { type:'success' });
    }catch(e){ 
      notify(e?.message||String(e), { type:'error' }); 
    }
  }

  if (!isStaff) return (
    <div className="card p-6 text-center">
      <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
      <p className="text-gray-600">You do not have permission to view delivery issues.</p>
      <p className="text-sm text-gray-500 mt-1">Required roles: admin, treasurer, or cashier</p>
    </div>
  );

  if (error) {
    return (
      <div className="card p-6 text-center">
        <h2 className="text-xl font-semibold text-red-900 mb-2">Error Loading Data</h2>
        <p className="text-red-700 mb-4">{error}</p>
        <button 
          onClick={() => window.location.reload()} 
          className="px-4 py-2 bg-red-100 text-red-800 rounded hover:bg-red-200"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h2 className="text-2xl font-bold">Delivery Issues</h2>
          {fallbackMode && (
            <p className="text-sm text-amber-600 mt-1">
              ⚠️ Using fallback mode - some recent issues may not be visible
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-ink/70">Status</label>
          <select className="input input-sm" value={statusFilter} onChange={(e)=>setStatusFilter(e.target.value)}>
            <option value="open">Open</option>
            <option value="closed">Closed</option>
            <option value="all">All</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="card p-6 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mb-2"></div>
          <p className="text-ink/60">Loading delivery issues...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="card p-6 text-center text-ink/60">
          <p className="text-lg">No issues found.</p>
          <p className="text-sm mt-1">All delivery issues are resolved or none have been reported.</p>
        </div>
      ) : (
        <>
          <div className="hidden md:block card p-0 overflow-x-auto">
            <table className="min-w-[900px] w-full text-sm stack-on-mobile">
              <thead className="bg-gray-50 text-ink/70">
                <tr>
                  <th className="p-2 text-left">When</th>
                  <th className="p-2 text-left">Delivery</th>
                  <th className="p-2 text-left">Category</th>
                  <th className="p-2 text-left">Message</th>
                  <th className="p-2 text-left">Rider</th>
                  <th className="p-2 text-left">Status</th>
                  <th className="p-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={`${r.deliveryId}_${r.id}`} className="border-t hover:bg-gray-50">
                    <td className="p-2 whitespace-nowrap">
                      {r.createdAt?.toDate ? 
                        `${fromNow(r.createdAt)} · ${formatDT(r.createdAt.toDate())}` : 
                        '-'
                      }
                    </td>
                    <td className="p-2 font-mono text-sm">{r.deliveryId || '—'}</td>
                    <td className="p-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        r.category === 'address_not_found' ? 'bg-red-100 text-red-800' :
                        r.category === 'duplicate_job' ? 'bg-yellow-100 text-yellow-800' :
                        r.category === 'cannot_contact_buyer' ? 'bg-blue-100 text-blue-800' :
                        r.category === 'unsafe_area' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {r.category?.replace(/_/g, ' ') || '—'}
                      </span>
                    </td>
                    <td className="p-2 max-w-[420px] truncate" title={r.message||''}>
                      {r.message || '—'}
                    </td>
                    <td className="p-2 font-mono text-sm">{r.riderUid || '—'}</td>
                    <td className="p-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        r.status === 'open' ? 'bg-green-100 text-green-800' :
                        r.status === 'closed' ? 'bg-gray-100 text-gray-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {r.status || '—'}
                      </span>
                    </td>
                    <td className="p-2">
                      <div className="flex items-center gap-2">
                        <button 
                          className="btn btn-xs btn-outline" 
                          onClick={()=>{ 
                            navigator.clipboard.writeText(r.deliveryId||''); 
                            notify('Delivery ID copied to clipboard', { type: 'success' });
                          }}
                        >
                          Copy ID
                        </button>
                        {r.status !== 'closed' && (
                          <button 
                            className="btn btn-xs btn-primary" 
                            onClick={()=>closeIssue(r)}
                          >
                            Close
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile view */}
          <div className="md:hidden space-y-3">
            {filtered.map(r => (
              <div key={`${r.deliveryId}_${r.id}`} className="card p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-mono text-sm text-gray-600">#{r.deliveryId}</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        r.status === 'open' ? 'bg-green-100 text-green-800' :
                        r.status === 'closed' ? 'bg-gray-100 text-gray-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {r.status || '—'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-1">
                      {r.createdAt?.toDate ? fromNow(r.createdAt) : '-'}
                    </p>
                    <p className="text-sm font-medium mb-2">{r.message || '—'}</p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span>Rider: {r.riderUid || '—'}</span>
                      <span>•</span>
                      <span>{r.category?.replace(/_/g, ' ') || '—'}</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <button 
                      className="btn btn-xs btn-outline" 
                      onClick={()=>{ 
                        navigator.clipboard.writeText(r.deliveryId||''); 
                        notify('Delivery ID copied to clipboard', { type: 'success' });
                      }}
                    >
                      Copy ID
                    </button>
                    {r.status !== 'closed' && (
                      <button 
                        className="btn btn-xs btn-primary" 
                        onClick={()=>closeIssue(r)}
                      >
                        Close
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
